import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { getCandidate } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { Avatar } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/candidates/$candidateId')({ component: CandidateDetailPage })

const fmtExp = (m: number | null) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)

function skillsOf(pj: unknown): string[] {
  const p = pj as { skills?: unknown; skillsetRaw?: unknown } | null
  if (Array.isArray(p?.skills) && p.skills.length) return p.skills.map(String)
  if (typeof p?.skillsetRaw === 'string') return p.skillsetRaw.split(',').map((s) => s.trim()).filter(Boolean)
  return []
}

function CandidateDetailPage() {
  const { candidateId } = Route.useParams()
  const { data, isLoading } = useQuery({ queryKey: ['candidate', candidateId], queryFn: () => getCandidate(candidateId) })
  const c = data && 'candidate' in data ? data.candidate : null

  return (
    <DashboardLayout title="Candidate" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !c ? (
        <div className="text-sm text-muted-foreground">Candidate not found.</div>
      ) : (
        <>
          <div className="mb-6 flex items-center gap-4">
            <Avatar name={c.name ?? '?'} className="h-12 w-12 text-sm" />
            <div>
              <h1 className="text-xl font-semibold text-foreground">{c.name ?? '—'}</h1>
              <p className="text-sm text-muted-foreground">
                {c.employer ?? '—'} · {fmtExp(c.totalMonthsExp)} · {c.location ?? '—'}
              </p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardContent className="pt-5 pb-5">
                <h2 className="mb-3 text-sm font-medium">Skills</h2>
                <div className="flex flex-wrap gap-1.5">
                  {skillsOf(c.parsedJson).map((s) => (
                    <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                      {s}
                    </span>
                  ))}
                  {skillsOf(c.parsedJson).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2 pt-5 pb-5 text-sm">
                <Field label="Email" value={c.email} />
                <Field label="Phone" value={c.phone} />
                <Field label="Current CTC" value={c.currentCtc} />
                <Field label="Expected CTC" value={c.expectedCtc} />
                <Field label="Notice" value={c.noticeDays != null ? `${c.noticeDays} days` : null} />
                <Field label="PAN" value={c.pan} />
                <Field label="DOB" value={c.dob} />
                <Field label="Preferred" value={c.preferredLocations?.join(', ')} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </DashboardLayout>
  )
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value || '—'}</span>
    </div>
  )
}
